import { ProjectDto } from 'tdc-web-backend/projects/schemas';
import { EnumCurrency } from 'tdc-web-backend/enums/enums';
import { useEffect, useState } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useGetMany from '../../../../../../../../utils/hooks/crud-hooks/useGetMany';
import useAuth from '../../../../../../../../utils/hooks/useAuth';
import { primaryLight, secondaryGreen } from '../../../../../../../../utils/color';
import CustomSwitchInput from '../../../../../../../../components/custom-switch-input/CustomSwitchInput';
import CustomTooltip from '../../../../../../../../components/tooltip/CustomTooltip';
import CustomNoRowsOverlay from '../../../../../../../../components/custom-no-rows-overlay/CustomNoRowsOverlay';

const Earnings = () => {
  const {
    authData: { userData },
  } = useAuth();
  const theme = useTheme();
  const isBetweenLgGr = useMediaQuery(theme.breakpoints.between('lg', 'gr'));
  const isBetweenGrXl = useMediaQuery(theme.breakpoints.between('gr', 'xl'));
  const isBetweenMdLg = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'hr' ? 'hr' : 'en-US';
  const [showEur, setShowEur] = useState<boolean>(true);
  const [showUsd, setShowUsd] = useState<boolean>(true);
  const [pieChartDataEur, setPieChartDataEur] = useState<Array<{ name: string; value: number }>>(
    [],
  );
  const [pieChartDataUsd, setPieChartDataUsd] = useState<Array<{ name: string; value: number }>>(
    [],
  );

  const totalAmountEur = pieChartDataEur.reduce((acc, curr) => acc + curr.value, 0);
  const totalAmountUsd = pieChartDataUsd.reduce((acc, curr) => acc + curr.value, 0);

  const chartRadiusUsd = {
    inner: totalAmountEur === 0 || !showEur ? '55%' : '38%',
    outer: totalAmountEur === 0 || !showEur ? '75%' : '58%',
  };

  const { data: projectsResponse } = useGetMany<ProjectDto>({
    resource: `/projects?company=${userData?.membership?.company}`,
    enabled: !!userData?.membership?.company,
  });

  const chartElelmentPositions: {
    cx: string | number;
    cy: string | number;
    leggendBottom: string | number;
  } = {
    cx: '50%',
    cy: '50%',
    leggendBottom: 0,
  };

  if (isBetweenMdLg) {
    chartElelmentPositions.cx = '50%';
    chartElelmentPositions.cy = '54%';
    chartElelmentPositions.leggendBottom = 0;
  } else if (isBetweenLgGr) {
    chartElelmentPositions.cx = '50%';
    chartElelmentPositions.cy = '57%';
    chartElelmentPositions.leggendBottom = '-80px';
  } else if (isBetweenGrXl) {
    chartElelmentPositions.cx = '54%';
    chartElelmentPositions.cy = '55%';
    chartElelmentPositions.leggendBottom = '-45px';
  }

  useEffect(() => {
    if (projectsResponse?.data.results) {
      let unpaidSumEur = 0;
      let paidSumEur = 0;
      let unpaidSumUsd = 0;
      let paidSumUsd = 0;
      projectsResponse.data.results.forEach((project) => {
        if (project.currency === EnumCurrency.Eur) {
          unpaidSumEur += project.unpaidMilestonesSum || 0;
          paidSumEur += project.paidMilestonesSum || 0;
        } else if (project.currency === EnumCurrency.Usd) {
          unpaidSumUsd += project.unpaidMilestonesSum || 0;
          paidSumUsd += project.paidMilestonesSum || 0;
        }
      });

      setPieChartDataEur([
        { name: 'paidEur', value: paidSumEur },
        { name: 'unpaidEur', value: unpaidSumEur },
      ]);

      setPieChartDataUsd([
        { name: 'paidUsd', value: paidSumUsd },
        { name: 'unpaidUsd', value: unpaidSumUsd },
      ]);
    }
  }, [projectsResponse]);

  return (
    <Stack sx={{ position: 'relative' }} height="100%" width="100%">
      <Stack width="100%" height="100%" justifyContent="end">
        <ResponsiveContainer minHeight="10px" width="100%" height="100%">
          <PieChart
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '500px',
              maxHeight: '25vh',
              minHeight: '100%',
              aspectRatio: 1,
            }}
          >
            <Legend
              wrapperStyle={{
                fontFamily: theme.typography.fontFamily,
                fontSize: '1rem',
                // bottom: chartElelmentPositions.leggendBottom,
              }}
              verticalAlign="bottom"
              height={15}
              formatter={(value, entry, index) => {
                const { color } = entry;
                let newValue = value;
                let title = value;
                switch (value) {
                  case 'paidEur':
                    newValue = `${t('chart.paidAbbr')} (€)`;
                    title = `${t('chart.paid')} (€)`;
                    break;
                  case 'paidUsd':
                    newValue = `${t('chart.paidAbbr')} ($)`;
                    title = `${t('chart.paid')} ($)`;
                    break;
                  case 'unpaidEur':
                    newValue = `${t('chart.unpaidAbbr')} (€)`;
                    title = `${t('chart.unpaid')} (€)`;
                    break;
                  case 'unpaidUsd':
                    newValue = `${t('chart.unpaidAbbr')} ($)`;
                    title = `${t('chart.unpaid')} ($)`;
                    break;
                  default:
                    break;
                }
                return (
                  <CustomTooltip title={title}>
                    <Typography variant="body3" sx={{ color, cursor: 'pointer' }}>
                      {newValue}
                    </Typography>
                  </CustomTooltip>
                );
              }}
            />

            {showEur && totalAmountEur > 0 && (
              <Pie
                data={pieChartDataEur}
                dataKey="value"
                cx={chartElelmentPositions.cx}
                cy={chartElelmentPositions.cy}
                innerRadius="55%"
                outerRadius="75%"
                label={(props) =>
                  new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency: 'EUR',
                    notation: 'compact',
                    maximumFractionDigits: 1,
                  }).format(props.value)
                }
                legendType="circle"
              >
                {pieChartDataEur.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.name === 'paidEur' ? primaryLight[500] : primaryLight[300]}
                  />
                ))}
              </Pie>
            )}
            {showUsd && totalAmountUsd > 0 && (
              <Pie
                data={pieChartDataUsd}
                dataKey="value"
                cx={chartElelmentPositions.cx}
                cy={chartElelmentPositions.cy}
                innerRadius={chartRadiusUsd.inner}
                outerRadius={chartRadiusUsd.outer}
                label={(props) =>
                  new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency: 'USD',
                    notation: 'compact',
                    maximumFractionDigits: 1,
                  }).format(props.value)
                }
                legendType="circle"
              >
                {pieChartDataUsd.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.name === 'paidUsd' ? secondaryGreen[801] : secondaryGreen[601]}
                  />
                ))}
              </Pie>
            )}
          </PieChart>
        </ResponsiveContainer>
      </Stack>
      {totalAmountEur > 0 && totalAmountUsd > 0 && (
        <Stack
          spacing={1.5}
          sx={{
            position: 'absolute',
            left: -15,
            top: -15,
            p: 2,
          }}
          direction={isBetweenLgGr ? 'row' : 'column'}
        >
          <CustomSwitchInput
            label="EUR"
            checked={showEur}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowEur(e.target.checked)}
          />
          <CustomSwitchInput
            label="USD"
            checked={showUsd}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowUsd(e.target.checked)}
            sx={{
              '& .MuiSwitch-switchBase': {
                padding: 0.2,
                transitionDuration: '300ms',
                '&.Mui-checked': {
                  transform: 'translateX(12px)',
                  color: '#fff',
                  '& + .MuiSwitch-track': {
                    opacity: 1,
                    border: 0,
                    backgroundColor: 'secondaryGreen.701',
                  },
                },
              },
            }}
          />
        </Stack>
      )}
      {totalAmountEur === 0 && totalAmountUsd === 0 && <CustomNoRowsOverlay />}
    </Stack>
  );
};

export default Earnings;
